import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Eye,
  Pencil,
  Search,
  Receipt,
  Loader2,
  AlertCircle,
  X,
  Filter
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';
import { ProductsPreview } from './ProductsPreview';
import { ProductAutocomplete } from './forms/ProductAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PurchaseBills = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Filters — order: Vendor, Product, Company
  const [fVendorId, setFVendorId] = useState('');
  const [fProduct, setFProduct] = useState('');
  const [fProductInput, setFProductInput] = useState('');
  const [showProductSuggest, setShowProductSuggest] = useState(false);
  const [fCompany, setFCompany] = useState('');
  const [fCompanyInput, setFCompanyInput] = useState('');
  const [showCompanySuggest, setShowCompanySuggest] = useState(false);
  // Date filter (bill date)
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [fActivePreset, setFActivePreset] = useState('');
  // Payment due date filter
  const [fDueFrom, setFDueFrom] = useState('');
  const [fDueTo, setFDueTo] = useState('');
  const [fDuePreset, setFDuePreset] = useState('');
  // Payment status filter
  const [fStatus, setFStatus] = useState('');
  const [applied, setApplied] = useState({ vendorId: '', product: '', company: '', dateFrom: '', dateTo: '', dueFrom: '', dueTo: '', status: '' });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchBills(); }, []);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const [billsRes, prodsRes, vendorsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/purchase-bills`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/vendors`, getAuthHeaders()),
      ]);
      setBills(billsRes.data);
      const map = {};
      (prodsRes.data || []).forEach(p => { map[p.id] = p.name; });
      setProductMap(map);
      setVendors(vendorsRes.data || []);
    } catch (err) {
      setError('Failed to fetch purchase bills');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/purchase-bills/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchBills();
    } catch (err) {
      setError('Failed to delete purchase bill');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  // Unique product names across all bills' line_items
  const allProductNames = [...new Set(
    bills.flatMap(b => (b.line_items || []).map(li => li.product_name || productMap[li.product_id]).filter(Boolean))
  )].sort();

  // Unique company names
  const companyNames = [...new Set(bills.map(b => b.company_name).filter(Boolean))].sort();

  const productSuggestions = fProductInput.trim()
    ? allProductNames.filter(n => n.toLowerCase().includes(fProductInput.toLowerCase()))
    : allProductNames.slice(0, 8);

  const companySuggestions = fCompanyInput.trim()
    ? companyNames.filter(n => n.toLowerCase().includes(fCompanyInput.toLowerCase()))
    : companyNames.slice(0, 8);

  const applyPreset = (preset) => {
    const { fd, td } = calcPreset(preset);
    setFActivePreset(preset); setFDateFrom(fd); setFDateTo(td);
  };

  const applyDuePreset = (preset) => {
    const { fd, td } = calcPreset(preset);
    setFDuePreset(preset); setFDueFrom(fd); setFDueTo(td);
  };

  function calcPreset(preset) {
    const now = new Date();
    let fd = '', td = '';
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (preset === 'this_month') {
      fd = fmt(new Date(now.getFullYear(), now.getMonth(), 1)); td = fmt(now);
    } else if (preset === 'last_month') {
      fd = fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      td = fmt(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (preset === 'this_quarter') {
      const qs = Math.floor(now.getMonth() / 3) * 3;
      fd = fmt(new Date(now.getFullYear(), qs, 1)); td = fmt(now);
    } else if (preset === 'last_quarter') {
      const qs = Math.floor(now.getMonth() / 3) * 3 - 3;
      const y = qs < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const qss = ((qs % 12) + 12) % 12;
      fd = fmt(new Date(y, qss, 1)); td = fmt(new Date(y, qss + 3, 0));
    } else if (preset === 'this_year') {
      fd = fmt(new Date(now.getFullYear(), 0, 1)); td = fmt(now);
    } else if (preset === 'last_year') {
      fd = fmt(new Date(now.getFullYear() - 1, 0, 1));
      td = fmt(new Date(now.getFullYear() - 1, 11, 31));
    }
    return { fd, td };
  }

  const applyFilters = () => setApplied({ vendorId: fVendorId, product: fProduct, company: fCompany, dateFrom: fDateFrom, dateTo: fDateTo, dueFrom: fDueFrom, dueTo: fDueTo, status: fStatus });
  const clearFilters = () => {
    setFVendorId(''); setFProduct(''); setFProductInput(''); setFCompany(''); setFCompanyInput('');
    setFDateFrom(''); setFDateTo(''); setFActivePreset('');
    setFDueFrom(''); setFDueTo(''); setFDuePreset('');
    setFStatus('');
    setApplied({ vendorId: '', product: '', company: '', dateFrom: '', dateTo: '', dueFrom: '', dueTo: '', status: '' });
  };
  const hasFilters = applied.vendorId || applied.product || applied.company || applied.dateFrom || applied.dateTo || applied.dueFrom || applied.dueTo || applied.status;

  const filtered = (() => {
    let result = bills.filter(b =>
      (b.bill_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.vendor_name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (applied.vendorId) result = result.filter(b => b.vendor_id === applied.vendorId);
    if (applied.product) result = result.filter(b =>
      (b.line_items || []).some(li => (li.product_name || productMap[li.product_id] || '') === applied.product)
    );
    if (applied.company) result = result.filter(b => b.company_name === applied.company);
    if (applied.dateFrom) result = result.filter(b => (b.bill_date || b.created_at || '') >= applied.dateFrom);
    if (applied.dateTo) result = result.filter(b => (b.bill_date || b.created_at || '') <= applied.dateTo + 'T23:59:59');
    if (applied.dueFrom) result = result.filter(b => b.payment_due_date && b.payment_due_date >= applied.dueFrom);
    if (applied.dueTo) result = result.filter(b => b.payment_due_date && b.payment_due_date <= applied.dueTo + 'T23:59:59');
    if (applied.status === 'paid') result = result.filter(b => (b.balance || 0) === 0);
    if (applied.status === 'partial') result = result.filter(b => (b.balance || 0) > 0 && (b.paid_amount || 0) > 0);
    if (applied.status === 'pending') result = result.filter(b => (b.paid_amount || 0) === 0);
    return result;
  })();

  return (
    <div data-testid="purchase-bills-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Purchase Bills</h1>
          <p className="text-slate-600 mt-1">Manage purchase bills and expenses</p>
        </div>
        {perms.canAdd && (
        <button
          onClick={() => navigate('/admin/purchase/bills/new')}
          data-testid="create-bill-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Create Purchase Bill
        </button>
        )}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by bill number or vendor..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          {/* Vendor */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Vendor</label>
            <ProductAutocomplete value={fVendorId} onChange={setFVendorId} products={vendors} testId="filter-vendor" placeholder="Type vendor..." />
          </div>

          {/* Product — type and autofill */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Product</label>
            <div className="relative">
              <input
                type="text"
                value={fProductInput}
                onChange={e => { setFProductInput(e.target.value); setFProduct(''); setShowProductSuggest(true); }}
                onFocus={() => setShowProductSuggest(true)}
                onBlur={() => setTimeout(() => setShowProductSuggest(false), 150)}
                placeholder="Type product..."
                data-testid="filter-product-input"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
              />
              {fProductInput && (
                <button onClick={() => { setFProduct(''); setFProductInput(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              )}
              {showProductSuggest && productSuggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-sm shadow-lg max-h-44 overflow-auto">
                  {productSuggestions.map(name => (
                    <button key={name} onMouseDown={() => { setFProduct(name); setFProductInput(name); setShowProductSuggest(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 ${fProduct === name ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'}`}>
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Company — type and autofill */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Company</label>
            <div className="relative">
              <input
                type="text"
                value={fCompanyInput}
                onChange={e => { setFCompanyInput(e.target.value); setFCompany(''); setShowCompanySuggest(true); }}
                onFocus={() => setShowCompanySuggest(true)}
                onBlur={() => setTimeout(() => setShowCompanySuggest(false), 150)}
                placeholder="Type company..."
                data-testid="filter-company-input"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
              />
              {fCompanyInput && (
                <button onClick={() => { setFCompany(''); setFCompanyInput(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              )}
              {showCompanySuggest && companySuggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-sm shadow-lg max-h-44 overflow-auto">
                  {companySuggestions.map(name => (
                    <button key={name} onMouseDown={() => { setFCompany(name); setFCompanyInput(name); setShowCompanySuggest(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 ${fCompany === name ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'}`}>
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Payment Status */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Payment Status</label>
            <select value={fStatus} onChange={e => setFStatus(e.target.value)}
              data-testid="filter-status"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
              <option value="">All</option>
              <option value="paid">Paid</option>
              <option value="partial">Partially Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Apply + Clear */}
          <div className="flex gap-2">
            <button onClick={applyFilters} data-testid="apply-filters-btn"
              className="flex-1 px-4 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">
              Apply
            </button>
            {hasFilters && (
              <button onClick={clearFilters} data-testid="clear-filters-btn"
                className="px-4 py-2 border border-slate-200 text-sm text-red-500 hover:text-red-600 hover:border-red-300 rounded-sm transition-colors font-medium">
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Bill Date Range Row */}
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Bill Date</p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: 'this_month',   label: 'This Month' },
                { key: 'last_month',   label: 'Last Month' },
                { key: 'this_quarter', label: 'This Quarter' },
                { key: 'last_quarter', label: 'Last Quarter' },
                { key: 'this_year',    label: 'This Year' },
                { key: 'last_year',    label: 'Last Year' },
              ].map(p => (
                <button key={p.key} onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${
                    fActivePreset === p.key
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="h-5 w-px bg-slate-200 hidden md:block" />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500 font-medium whitespace-nowrap">From</label>
                <input type="date" value={fDateFrom}
                  onChange={e => { setFDateFrom(e.target.value); setFActivePreset(''); }}
                  data-testid="date-from"
                  className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500 font-medium">To</label>
                <input type="date" value={fDateTo}
                  onChange={e => { setFDateTo(e.target.value); setFActivePreset(''); }}
                  data-testid="date-to"
                  className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Payment Due Date Range Row */}
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Payment Due Date</p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: 'this_month',   label: 'This Month' },
                { key: 'last_month',   label: 'Last Month' },
                { key: 'this_quarter', label: 'This Quarter' },
                { key: 'last_quarter', label: 'Last Quarter' },
                { key: 'this_year',    label: 'This Year' },
                { key: 'last_year',    label: 'Last Year' },
              ].map(p => (
                <button key={p.key} onClick={() => applyDuePreset(p.key)} data-testid={`due-preset-${p.key}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${
                    fDuePreset === p.key
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="h-5 w-px bg-slate-200 hidden md:block" />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500 font-medium whitespace-nowrap">From</label>
                <input type="date" value={fDueFrom}
                  onChange={e => { setFDueFrom(e.target.value); setFDuePreset(''); }}
                  data-testid="due-date-from"
                  className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-blue-500 focus:outline-none" />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500 font-medium">To</label>
                <input type="date" value={fDueTo}
                  onChange={e => { setFDueTo(e.target.value); setFDuePreset(''); }}
                  data-testid="due-date-to"
                  className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-blue-500 focus:outline-none" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Receipt className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No purchase bills yet</h3>
            <p className="text-slate-500 mb-6">Create your first purchase bill</p>
            <button
              onClick={() => navigate('/admin/purchase/bills/new')}
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Create First Bill
            </button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-8 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Bill No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Vendor</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Company</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Total</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Balance</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map((bill) => (
              <div key={bill.id} className="border-b border-slate-100 hover:bg-slate-50">
                <div
                  data-testid={`bill-row-${bill.id}`}
                  className="grid grid-cols-1 md:grid-cols-8 gap-4 px-6 pt-4 items-center"
                >
                  <div className="text-slate-600 text-sm">{formatDate(bill.bill_date || bill.created_at)}</div>
                  <div className="col-span-2 font-mono font-medium text-slate-900">{bill.bill_number || '-'}</div>
                  <div className="text-slate-700 text-sm">{bill.vendor_name || '-'}</div>
                  <div className="text-slate-700 text-sm">{bill.company_name || '-'}</div>
                  <div className="font-medium text-slate-900">₹{bill.total?.toLocaleString()}</div>
                  <div>
                    <div className={`font-medium ${bill.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ₹{bill.balance?.toLocaleString()}
                    </div>
                    {bill.balance > 0 && bill.payment_due_date && (() => {
                      const today = new Date(); today.setHours(0,0,0,0);
                      const due = new Date(bill.payment_due_date); due.setHours(0,0,0,0);
                      const diff = Math.round((due - today) / 86400000);
                      return diff < 0
                        ? <span className="text-xs font-semibold text-red-500">Overdue by {Math.abs(diff)} day{Math.abs(diff) !== 1 ? 's' : ''}</span>
                        : diff === 0
                          ? <span className="text-xs font-semibold text-orange-500">Due today</span>
                          : <span className="text-xs text-slate-500">Payable in {diff} day{diff !== 1 ? 's' : ''}</span>;
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/admin/purchase/bills/${bill.id}`)}
                      data-testid={`view-btn-${bill.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors" title="View">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button onClick={() => navigate(`/admin/purchase/bills/${bill.id}/edit`)}
                      data-testid={`edit-btn-${bill.id}`}
                      className="p-2 text-slate-600 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => setDeleteConfirm(bill.id)}
                      data-testid={`delete-btn-${bill.id}`}
                      className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this purchase bill?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
